<script setup lang="ts">
/**
 * RouteDenialNotice — ops renders the role bounce the shared guard records
 * (V-FE L2).
 *
 * A developer opening Bulk or Scheduled Reports (staff + admin only, the
 * backend's `require_roles`) was sent to `/file-review` with no word about
 * why. The guard already keeps the denial — reason, who grants access, the
 * dropped URL — in `denial-state.ts`, and that module says each app renders it
 * with what it has; ops had rendered nothing. This is the ops half, mounted
 * once in `App.vue`, drawing the kit's `DenialView`.
 *
 * Lifetime: read once (`consumeRouteDenial`) on the navigation that lands on
 * the bounce target, shown on that page only, gone on the next navigation, on
 * dismiss, or when the signed-in user changes.
 */
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { consumeRouteDenial, useAuthStore, type RouteDenial } from '@claimshield/shared'
import DenialView from './atlas/kit/DenialView.vue'

const router = useRouter()
const auth = useAuthStore()

const shown = ref<RouteDenial | null>(null)
let landedAt: string | null = null

function take(landing: string): boolean {
  const denial = consumeRouteDenial()
  if (!denial) return false
  shown.value = denial
  landedAt = landing
  return true
}

function dismiss(): void {
  shown.value = null
  landedAt = null
}

const stop = router.afterEach((to) => {
  if (take(to.fullPath)) return
  if (shown.value && to.fullPath !== landedAt) dismiss()
})

// A bounce on the very first navigation can finish before this mounts.
onMounted(async () => {
  await router.isReady()
  if (!shown.value) take(router.currentRoute.value.fullPath)
})

watch(
  () => auth.user?.id ?? null,
  (next, prev) => {
    if (next !== prev) dismiss()
  },
)

// App.vue lives as long as the page; the unhook is for tests that mount twice.
onBeforeUnmount(stop)
</script>

<template>
  <div
    v-if="shown"
    class="route-denial-notice"
    role="alert"
    data-testid="route-denial-notice"
  >
    <DenialView :denial="shown" />
    <button
      type="button"
      class="route-denial-dismiss"
      aria-label="Dismiss"
      data-testid="route-denial-dismiss"
      @click="dismiss"
    >
      <i class="pi pi-times" aria-hidden="true" />
    </button>
  </div>
</template>

<style scoped>
.route-denial-notice {
  position: fixed;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  width: min(32rem, calc(100vw - 2rem));
  padding: 0 2.5rem 0 1rem;
  background: var(--p-content-background, #fff);
  border: 1px solid var(--p-content-border-color, #e2e8f0);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgb(0 0 0 / 0.12);
}
.route-denial-notice :deep(.atlas-denial-view) {
  padding-top: 1.25rem;
  padding-bottom: 1.25rem;
}
.route-denial-dismiss {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  padding: 0.375rem;
  border-radius: 8px;
  color: var(--p-text-muted-color, #64748b);
}
.route-denial-dismiss:hover {
  background: var(--p-content-hover-background, #f1f5f9);
}
</style>
